import {inject, Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy} from '@angular/router';
import {ClientStateService} from './auth/client-state.service';

@Injectable()
export class CustomRouteReuseStrategy implements RouteReuseStrategy {
    private readonly clientStateService = inject(ClientStateService);
    private clientContextResetPending = false;
    private clientContextResetEpoch = 0;
    private scheduledResetEpoch: number | null = null;

    constructor() {
        this.clientStateService.resetCache$.subscribe(reset => {
            if (reset) {
                this.clientContextResetPending = true;
                this.clientContextResetEpoch++;
            }
        });
    }

    // Never detach any routes
    shouldDetach(_route: ActivatedRouteSnapshot): boolean {
        return false;
    }

    store(_route: ActivatedRouteSnapshot, _handle: DetachedRouteHandle | null): void {
        // No-op
    }
    // Never attach a stored route
    shouldAttach(_route: ActivatedRouteSnapshot): boolean {
        return false;
    }

    retrieve(_route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
        return null;
    }

    // Customize which routes keep their component instance alive across navigation.
    shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
        // A client switch must recreate every active route component before it can
        // read or write tenant-scoped state for the newly selected client.
        if (this.clientContextResetPending) {
            this.consumeClientContextResetAfterNavigation();
            return false;
        }

        const futurePath = future.routeConfig?.path;
        const currPath = curr.routeConfig?.path;

        // Keep the workflow canvas instance alive when switching between edit/run
        // so unsaved canvas changes aren't lost during run visualization.
        const isConnectWorkflowCanvas =
            (futurePath === 'connect/workflows/:id/edit' || futurePath === 'connect/workflows/:id/run/:runId') &&
            (currPath === 'connect/workflows/:id/edit' || currPath === 'connect/workflows/:id/run/:runId');
        if (isConnectWorkflowCanvas) return true;

        // Do not reuse /dashboard, otherwise reuse.
        return !(futurePath === 'dashboard' && currPath === 'dashboard') && future.routeConfig === curr.routeConfig;
    }

    private consumeClientContextResetAfterNavigation(): void {
        const resetEpoch = this.clientContextResetEpoch;
        if (this.scheduledResetEpoch === resetEpoch) return;

        this.scheduledResetEpoch = resetEpoch;
        setTimeout(() => {
            if (this.clientContextResetEpoch === resetEpoch) {
                this.clientContextResetPending = false;
            }
            if (this.scheduledResetEpoch === resetEpoch) {
                this.scheduledResetEpoch = null;
            }
        }, 0);
    }
}
